'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMediaQuery } from '@/app/hooks/useMediaQuery';
import { SPARTAN_FEATURE_SHOWCASE_EXPAND_MIN_HEIGHT_VAR, SPARTAN_FEATURE_SHOWCASE_TITLE_HEIGHT_VAR } from '@/app/lcv/components/sections/spartan/products/feature-showcase/constants';
import { SpartanProductFeatureShowcaseAccordionItem } from '@/app/lcv/components/sections/spartan/products/feature-showcase/panel/accordion-item';
import { SpartanProductFeatureShowcaseMedia } from '@/app/lcv/components/sections/spartan/products/feature-showcase/panel/media';
import type { SpartanProductFeatureShowcasePanelProps } from '@/app/lcv/components/sections/spartan/products/feature-showcase/panel/types';
import { getSpartanProductFeatureShowcaseTracking } from '@/app/lcv/components/sections/spartan/products/feature-showcase/tracking';
import { useSpartanScrollRefresh } from '@/app/lcv/components/sections/spartan/products/shared/product-scroll/useSpartanScrollRefresh';
import { getSpartanRevealGroupProps, getSpartanRevealItemProps, SPARTAN_REVEAL_PRESET } from '@/app/lcv/components/sections/spartan/products/shared/reveal';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
export const SpartanProductFeatureShowcasePanel = ({
  features,
  trackingPrefix
}: SpartanProductFeatureShowcasePanelProps) => {
  const tracking = getSpartanProductFeatureShowcaseTracking(trackingPrefix);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [expandHeight, setExpandHeight] = useState<number>(0);
  const isDesktop = !!useMediaQuery('(min-width: 1280px)', false);
  const featureListRef = useRef<HTMLDivElement | null>(null);
  const imageWrapRef = useRef<HTMLDivElement | null>(null);
  const contentRefs = useRef<(HTMLDivElement | null)[]>([]);
  const appliedExpandHeightRef = useRef(0);
  const appliedTitleHeightRef = useRef(0);
  const measureFrameRef = useRef<number | null>(null);
  const refreshScroll = useSpartanScrollRefresh();
  const contentRefCallbacks = useMemo(() => features.map((_, index) => (node: HTMLDivElement | null) => {
    contentRefs.current[index] = node;
  }), [features]);
  const handleAnimationComplete = useCallback(() => {
    refreshScroll();
  }, [refreshScroll]);
  useEffect(() => {
    contentRefs.current.length = features.length;
    const list = featureListRef.current;
    const imageWrap = imageWrapRef.current;
    if (!isDesktop) {
      list?.style.removeProperty(SPARTAN_FEATURE_SHOWCASE_EXPAND_MIN_HEIGHT_VAR);
      list?.style.removeProperty(SPARTAN_FEATURE_SHOWCASE_TITLE_HEIGHT_VAR);
      appliedExpandHeightRef.current = 0;
      appliedTitleHeightRef.current = 0;
      setExpandHeight(0);
      refreshScroll();
      return;
    }
    const applyHeights = () => {
      measureFrameRef.current = null;
      if (!list) return;
      const titleCount = features.length;
      if (titleCount === 0) return;

      // Prefer image column height so title/content writes don't feedback into RO.
      const panelHeight = imageWrap?.clientHeight || list.clientHeight;
      if (panelHeight <= 0) return;
      const maxContentHeight = Math.round(Math.max(0, ...contentRefs.current.map(node_0 => node_0?.scrollHeight ?? 0)));
      const styles = getComputedStyle(list);
      const paddingY = (parseFloat(styles.paddingTop) || 0) + (parseFloat(styles.paddingBottom) || 0);
      const dividersHeight = Math.max(0, titleCount - 1); // 1px border-b each

      // Floor so titles×n + content never exceeds panel (avoids RO loop / scrollbar).
      const titleHeight = Math.max(0, Math.floor((panelHeight - paddingY - dividersHeight - maxContentHeight) / titleCount));
      if (maxContentHeight === appliedExpandHeightRef.current && titleHeight === appliedTitleHeightRef.current) {
        return;
      }
      appliedExpandHeightRef.current = maxContentHeight;
      appliedTitleHeightRef.current = titleHeight;
      if (maxContentHeight > 0) {
        list.style.setProperty(SPARTAN_FEATURE_SHOWCASE_EXPAND_MIN_HEIGHT_VAR, `${maxContentHeight}px`);
      } else {
        list.style.removeProperty(SPARTAN_FEATURE_SHOWCASE_EXPAND_MIN_HEIGHT_VAR);
      }
      list.style.setProperty(SPARTAN_FEATURE_SHOWCASE_TITLE_HEIGHT_VAR, `${titleHeight}px`);
      setExpandHeight(maxContentHeight);
      refreshScroll();
    };
    const scheduleMeasure = () => {
      if (measureFrameRef.current !== null) {
        window.cancelAnimationFrame(measureFrameRef.current);
      }
      measureFrameRef.current = window.requestAnimationFrame(applyHeights);
    };
    scheduleMeasure();
    if (typeof ResizeObserver === 'undefined') {
      return () => {
        if (measureFrameRef.current !== null) {
          window.cancelAnimationFrame(measureFrameRef.current);
        }
      };
    }
    const observer = new ResizeObserver(scheduleMeasure);
    if (imageWrap) observer.observe(imageWrap);
    // Width changes reflow text; observe contents but defer writes via rAF.
    for (const node_1 of contentRefs.current) {
      if (node_1) observer.observe(node_1);
    }
    return () => {
      observer.disconnect();
      if (measureFrameRef.current !== null) {
        window.cancelAnimationFrame(measureFrameRef.current);
      }
    };
  }, [features, isDesktop, refreshScroll]);
  const handleToggleFeature = (index_0: number) => {
    if (index_0 === selectedIndex) {
      setSelectedIndex(index_0 === features.length - 1 ? 0 : index_0 + 1);
      return;
    }
    setSelectedIndex(index_0);
  };
  return <div className="grid xl:grid-cols-12 xl:items-stretch xl:gap-x-6" {...getSpartanRevealGroupProps(SPARTAN_REVEAL_PRESET.media)}>
      {!!isDesktop && <div className="col-span-8 col-start-1 w-full" {...getSpartanRevealItemProps()} {...createDataAttributes({
      test: tracking.desktopImageWrap
    })}>
          <div ref={imageWrapRef} className="aspect-5-4 relative w-full">
            {features.map((feature, index_1) => {
          const isActive = index_1 === selectedIndex;
          return <div key={[feature.title, index_1].join('-')} aria-hidden={!isActive} className={cn('ease-bounce absolute inset-0 transition-opacity duration-[1.2s]', 'motion-reduce:transition-none', isActive ? 'z-10 opacity-100' : 'pointer-events-none z-0 opacity-0')}>
                  <SpartanProductFeatureShowcaseMedia src={feature.image.src} alt={isActive ? feature.title : ''} width={1104} height={883} sizes="776px" className="size-full" testId={tracking.desktopImage(index_1)} />
                </div>;
        })}
          </div>
        </div>}

      <div ref={featureListRef} className="bg-silver-50 where-span:text-inline-wrap where-small:text-body-2-fixed flex w-full flex-col justify-start overflow-hidden rounded-lg px-4 py-2 md:px-6 md:py-3 xl:col-span-4 xl:col-start-9 xl:h-full xl:min-h-0 xl:px-6 xl:py-2" {...getSpartanRevealItemProps({
      order: 1
    })} {...createDataAttributes({
      test: tracking.featureList
    })}>
        {features.map((feature_0, index_2) => <SpartanProductFeatureShowcaseAccordionItem key={[feature_0.title, index_2].join('-')} feature={feature_0} index={index_2} isOpen={index_2 === selectedIndex} isDesktop={isDesktop} isLast={index_2 === features.length - 1} expandHeight={expandHeight} contentRef={contentRefCallbacks[index_2]} onToggle={handleToggleFeature} onAnimationComplete={handleAnimationComplete} trackingPrefix={trackingPrefix} />)}
      </div>
    </div>;
};